import useScrollAnimation from '../../../hooks/useScrollAnimation';

const aiFeatures = [
  {
    icon: 'ri-brain-line',
    title: 'Análisis inteligente en tiempo real',
    desc: 'La IA monitorea tu inventario constantemente y detecta patrones: qué se agota rápido, qué lleva semanas sin moverse y qué deberías reponer ya.',
    color: 'emerald',
  },
  {
    icon: 'ri-file-chart-line',
    title: 'Resumen completo de tu negocio',
    desc: 'Un informe claro y visual con los datos más importantes: ventas, stock crítico, productos estrella y alertas pendientes. Todo en un solo vistazo.',
    color: 'amber',
  },
  {
    icon: 'ri-file-pdf-2-line',
    title: 'Exporta a PDF con un clic',
    desc: 'Genera reportes profesionales en segundos. Compártelos con tu equipo, tu contador o guárdalos para tu registro mensual.',
    color: 'red',
  },
];

const colorMap: Record<string, { bg: string; text: string; border: string }> = {
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
  red: { bg: 'bg-red-100', text: 'text-red-500', border: 'border-red-200' },
};

const metrics = [
  { label: 'Productos en stock OK', value: '87%', icon: 'ri-checkbox-circle-line', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  { label: 'Alertas de escasez', value: '3', icon: 'ri-alarm-warning-line', color: 'text-amber-500', bg: 'bg-amber-50' },
  { label: 'Ventas este mes', value: 'S/4,820', icon: 'ri-money-cny-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
];

const AISection = () => {
  const { ref, visible } = useScrollAnimation();

  return (
    <section id="ia" className="py-16 sm:py-24 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-500" />
      <div className="absolute top-10 right-0 w-72 h-72 bg-emerald-50 rounded-full blur-3xl pointer-events-none opacity-60" />
      <div className="absolute bottom-10 left-0 w-56 h-56 bg-amber-50 rounded-full blur-3xl pointer-events-none opacity-60" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        {/* Header */}
        <div ref={ref} className={`text-center mb-12 sm:mb-16 ${visible ? 'anim-fade-up' : 'scroll-hidden'}`}>
          <div className="inline-flex items-center gap-2 bg-emerald-500 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            <i className="ri-sparkling-2-line" />
            Nueva Función — Inteligencia Artificial
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-gray-900 leading-tight mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>
            Tu IA personal que{' '}
            <span className="text-emerald-600">entiende tu negocio</span>{' '}
            y te dice qué hacer.
          </h2>
          <p className="text-gray-500 text-sm sm:text-lg max-w-2xl mx-auto">
            Ya no tienes que adivinar. La inteligencia artificial analiza tu stock, genera resúmenes automáticos y te los entrega en un PDF listo para compartir.
          </p>
        </div>

        {/* Main content */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-14 items-center mb-12 sm:mb-16">

          {/* Left: Feature list */}
          <div className={`flex-1 min-w-0 flex flex-col gap-5 sm:gap-6 ${visible ? 'anim-fade-up-1' : 'scroll-hidden'}`}>
            {aiFeatures.map((f) => {
              const c = colorMap[f.color];
              return (
                <div key={f.title} className={`flex items-start gap-4 bg-white border ${c.border} rounded-2xl p-5 sm:p-6`}>
                  <div className={`w-11 h-11 flex items-center justify-center ${c.bg} ${c.text} rounded-xl flex-shrink-0`}>
                    <i className={`${f.icon} text-xl`} />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1">{f.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right: AI Summary Mock Card */}
          <div className={`w-full lg:w-[46%] flex-shrink-0 ${visible ? 'anim-fade-up-2' : 'scroll-hidden'}`}>
            <div className="bg-gray-50 border border-gray-200 rounded-3xl p-5 sm:p-6">
              {/* Card header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-500 rounded-lg">
                    <i className="ri-brain-line text-white text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-800">Resumen IA — Tienda La Victoria</p>
                    <p className="text-xs text-gray-400">Generado hoy, 09:14 am</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full inline-block animate-pulse" />
                  En vivo
                </span>
              </div>

              {/* Metrics row */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
                {metrics.map((m) => (
                  <div key={m.label} className={`${m.bg} rounded-2xl p-3 sm:p-4 text-center`}>
                    <div className="flex items-center justify-center mb-1.5">
                      <i className={`${m.icon} ${m.color} text-lg sm:text-xl`} />
                    </div>
                    <p className={`text-lg sm:text-2xl font-black ${m.color}`} style={{ fontFamily: "'Syne', sans-serif" }}>{m.value}</p>
                    <p className="text-xs text-gray-500 leading-tight mt-1">{m.label}</p>
                  </div>
                ))}
              </div>

              {/* AI Recommendation */}
              <div className="bg-white border border-emerald-100 rounded-2xl p-4 mb-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-500 rounded-lg flex-shrink-0">
                    <i className="ri-sparkling-2-line text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-800 mb-1">Recomendación de la IA</p>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      "Reponer <strong className="text-gray-800">Pantalones Talla S</strong> en los próximos 3 días. Quedan solo 2 unidades y el historial indica que se agotan en 4 días."
                    </p>
                  </div>
                </div>
              </div>

              {/* Top sellers preview */}
              <div className="mb-5">
                <p className="text-xs font-bold text-gray-700 mb-3">Top productos esta semana</p>
                <div className="flex flex-col gap-2">
                  {[
                    { name: 'Camisas Talla M', pct: 84, color: 'bg-emerald-400' },
                    { name: 'Zapatillas Blancas', pct: 61, color: 'bg-amber-400' },
                    { name: 'Jeans Slim Fit', pct: 47, color: 'bg-orange-400' },
                  ].map((p) => (
                    <div key={p.name} className="flex items-center gap-3">
                      <p className="text-xs text-gray-600 w-32 sm:w-36 flex-shrink-0 truncate">{p.name}</p>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div className={`h-2 rounded-full ${p.color} transition-all`} style={{ width: `${p.pct}%` }} />
                      </div>
                      <span className="text-xs font-bold text-gray-500 w-8 text-right">{p.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Export PDF button */}
              <button className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-bold py-3 rounded-xl transition-colors cursor-pointer">
                <i className="ri-file-pdf-2-line text-red-400 text-base" />
                Exportar Reporte en PDF
                <i className="ri-download-line text-base" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom CTA banner */}
        <div className={`bg-gray-900 rounded-2xl px-5 sm:px-10 py-7 sm:py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 ${visible ? 'anim-fade-up-3' : 'scroll-hidden'}`}>
          <div className="flex items-start sm:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-xl flex-shrink-0">
              <i className="ri-sparkling-2-line text-white text-2xl" />
            </div>
            <div>
              <p className="text-white font-bold text-base sm:text-lg leading-snug">
                Todo esto incluido en tu acceso por <span className="text-emerald-400">S/20</span>.
              </p>
              <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                IA + reportes + PDF + alertas + historial. Sin pagar extra.
              </p>
            </div>
          </div>
          <button
            onClick={() => document.getElementById('checkout')?.scrollIntoView({ behavior: 'smooth' })}
            className="flex-shrink-0 bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-sm px-6 py-3 rounded-xl transition-colors cursor-pointer w-full sm:w-auto text-center leading-snug"
          >
            Quiero acceso completo por S/20
          </button>
        </div>
      </div>
    </section>
  );
};

export default AISection;
